/**
 * Success and error wording, and the mapping from a server error code to the
 * sentence a person reads.
 *
 * The backend returns a stable machine code plus a developer-facing message
 * (see NodeJS/src/core/dispatch.js). Rendering that raw message would leak
 * server vocabulary into the UI and change under us whenever the backend is
 * reworded, so every code the API can return is translated here instead. The
 * codes themselves are the contract; the English is ours.
 *
 * `errorMessageFor` is the only function the UI should call — it always returns
 * something printable, so no screen has to guard against an unmapped code.
 */

export const successMessages = {
  loginSucceeded: 'Signed in successfully.',
  loggedOut: 'You have been signed out.',
  saved: 'Saved successfully.'
};

export const errorMessages = {
  // Validation and request shape
  VALIDATION_ERROR: 'Please check the details you entered and try again.',
  UNKNOWN_ACTION: 'That operation is not available on this server.',
  TOO_LARGE: 'That request was too large to send.',

  // Sign-in and session
  INVALID_CREDENTIALS: 'That user ID or password is not correct.',
  ACCOUNT_LOCKED: 'This account is locked after too many failed attempts. Try again later.',
  ACCOUNT_DISABLED: 'This account has been disabled. Contact an administrator.',
  UNAUTHENTICATED: 'Please sign in to continue.',
  INVALID_TOKEN: 'Your session is no longer valid. Please sign in again.',
  INVALID_REFRESH_TOKEN: 'Your session has expired. Please sign in again.',
  PASSWORD_UNCHANGED: 'The new password must be different from the current one.',

  // Authorisation
  FORBIDDEN: 'You do not have access to that.',
  SYSTEM_ROLE: 'Built-in roles cannot be changed.',
  CANNOT_DELETE_SELF: 'You cannot delete your own account.',
  CANNOT_DEMOTE_SELF: 'You cannot change your own role.',
  CANNOT_DISABLE_SELF: 'You cannot disable your own account.',
  CANNOT_REMOVE_ADMIN_TILE: 'The admin section cannot be removed from this role.',
  LAST_ADMIN: 'The last administrator cannot be removed.',

  // Records
  NOT_FOUND: 'We could not find what you asked for.',
  USER_ID_TAKEN: 'That user ID is already in use.',
  EMAIL_TAKEN: 'That email address is already in use.',
  ROLE_KEY_TAKEN: 'That role key is already in use.',
  ROLE_IN_USE: 'That role is still assigned to somebody and cannot be removed.',
  UNKNOWN_ROLE: 'That role does not exist.',
  INSUFFICIENT_CASH_IN: 'There is not enough cash in to cover this.',

  // Transport and encryption
  RATE_LIMITED: 'Too many attempts. Please wait a moment and try again.',
  PAYLOAD_DECRYPT_FAILED: 'The server could not read that request. Please try again.',
  NETWORK_ERROR: 'Could not reach the server. Check your connection and try again.',
  HTTP_ERROR: 'The server responded unexpectedly. Please try again.',
  CONFIG_ERROR: 'The application is not configured correctly. Contact an administrator.',
  UNKNOWN_ERROR: 'Something went wrong. Please try again.'
};

/** The message shown when a code arrives that this build has never heard of. */
export const FALLBACK_ERROR_MESSAGE = errorMessages.UNKNOWN_ERROR;

/**
 * Turns an error code into a sentence.
 *
 * Accepts the code on its own or an ApiError-shaped object, because callers in
 * a catch block have the error, not the code, and unwrapping it at every call
 * site is how one of them ends up rendering "[object Object]".
 */
export function errorMessageFor(codeOrError) {
  if (!codeOrError) return FALLBACK_ERROR_MESSAGE;

  const code = typeof codeOrError === 'string' ? codeOrError : codeOrError.code;
  return errorMessages[code] || FALLBACK_ERROR_MESSAGE;
}

/**
 * Field-level validation wording, used by the yup schemas.
 *
 * Functions rather than plain strings where a bound is involved, so the number
 * lives in the schema — its natural home — and the sentence lives here.
 */
export const validationMessages = {
  required: (field) => `${field} is required.`,
  minLength: (field, min) => `${field} must be at least ${min} characters.`,
  maxLength: (field, max) => `${field} must be at most ${max} characters.`,
  pattern: (field) => `${field} contains characters that are not allowed.`,
  email: () => 'Enter a valid email address.'
};
